import { FC } from 'react';
import { GameCardContainerType } from '../../GameCard/GameCard.types';
import { Grid, GridItem } from '../../../shared/Grid';
import { useBreakpointInfo, MediaBreakpoint } from '../../../../hooks/useDeviceInfo';
import { joinStrings } from '../../../../utils/string';
import { renderGameCard, BaseLayoutProps } from '../utils';
import {
  COMPOSITE_LAYOUT_GAMES_COUNT,
  GRID_LAYOUT_GAMES_COUNT,
  HERO_LAYOUT_GAMES_COUNT,
} from '../../../../utils/games/createTileRowCollections';

const TALL_PLUS_GRID_MAX_TALL_INDEX = 4;
const HERO_PLUS_TALL_MAX_TALL_INDEX = 1;

type CompositeLayoutProps = BaseLayoutProps;

export const TallPlusGridLayout: FC<CompositeLayoutProps & { reverse?: boolean }> = ({
  games,
  getCompositeKey,
  reverse = false,
  ...props
}) => {
  const isMobileDevice = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]);

  if (games.length < COMPOSITE_LAYOUT_GAMES_COUNT) return null;

  const tallIndex = reverse ? Math.min(TALL_PLUS_GRID_MAX_TALL_INDEX, games.length - 1) : 0;
  const tallGame = games[tallIndex];

  if (!tallGame) return null;

  const gridGames = games.filter((_, index) => index !== tallIndex).slice(0, GRID_LAYOUT_GAMES_COUNT);

  if (gridGames.length < GRID_LAYOUT_GAMES_COUNT) return null;

  if (isMobileDevice) {
    // Mobile: TALL_PLUS_GRID → WIDE + GRID, GRID_PLUS_TALL → GRID + WIDE
    const wideCard = renderGameCard(tallGame, GameCardContainerType.Wide, 'composite-wide', props);
    const gridCard = (
      <div className="composite-grid-section">
        <div className="manual-grid">
          {gridGames.map((game, index) => (
            <div key={getCompositeKey(game, `composite-grid-${index}`)} className="manual-grid-item">
              {renderGameCard(game, GameCardContainerType.Box, `composite-grid-${index}`, props)}
            </div>
          ))}
        </div>
      </div>
    );

    const mobileItems = reverse
      ? [
          <GridItem key="grid-section" className="composite-mobile__grid">
            {gridCard}
          </GridItem>,
          <GridItem key={getCompositeKey(tallGame, 'composite-mobile-wide-reverse')} className="composite-mobile__wide">
            {wideCard}
          </GridItem>,
        ]
      : [
          <GridItem key={getCompositeKey(tallGame, 'composite-mobile-wide')} className="composite-mobile__wide">
            {wideCard}
          </GridItem>,
          <GridItem key="grid-section" className="composite-mobile__grid">
            {gridCard}
          </GridItem>,
        ];

    return (
      <Grid columns={1} gap="s" className="game-grid game-grid--composite-mobile">
        {mobileItems}
      </Grid>
    );
  }

  return (
    <Grid
      columns={3}
      gap="s"
      className={joinStrings([
        'game-grid',
        'game-grid--tall-plus-grid',
        reverse && 'game-grid--tall-plus-grid--reverse',
      ])}
    >
      <GridItem key={getCompositeKey(tallGame, 'tall-plus-grid-tall')} className="tall-plus-grid__tall">
        {renderGameCard(tallGame, GameCardContainerType.Tall, 'tall-plus-grid-tall', props)}
      </GridItem>
      {gridGames.map((game, index) => (
        <GridItem
          key={getCompositeKey(game, `tall-plus-grid-grid-${index + 1}`)}
          className={joinStrings(['tall-plus-grid__grid-item', `tall-plus-grid__grid-item--${index + 1}`])}
        >
          {renderGameCard(game, GameCardContainerType.Box, `tall-plus-grid-grid-${index + 1}`, props)}
        </GridItem>
      ))}
    </Grid>
  );
};

export const HeroPlusTallLayout: FC<CompositeLayoutProps & { reverse?: boolean }> = ({
  games,
  getCompositeKey,
  reverse = false,
  ...props
}) => {
  const isMobileDevice = useBreakpointInfo([MediaBreakpoint.BELOW_MID_DESKTOP]);

  if (games.length === 0) return null;

  const heroIndex = reverse && games.length > HERO_LAYOUT_GAMES_COUNT ? 1 : 0;
  const tallIndex =
    reverse && games.length > HERO_LAYOUT_GAMES_COUNT ? 0 : Math.min(HERO_PLUS_TALL_MAX_TALL_INDEX, games.length - 1);
  const heroGame = games[heroIndex];
  const tallGame = games[tallIndex] || heroGame;

  if (!heroGame) return null;

  if (isMobileDevice) {
    const heroCard = renderGameCard(heroGame, GameCardContainerType.Box, 'composite-hero', props);
    const wideCard = renderGameCard(tallGame, GameCardContainerType.Wide, 'composite-wide', props);

    const mobileItems = reverse
      ? [
          <GridItem
            key={getCompositeKey(tallGame, 'hero-composite-mobile-wide-reverse')}
            className="composite-mobile__wide"
          >
            {wideCard}
          </GridItem>,
          <GridItem
            key={getCompositeKey(heroGame, 'hero-composite-mobile-hero-reverse')}
            className="composite-mobile__hero"
          >
            {heroCard}
          </GridItem>,
        ]
      : [
          <GridItem key={getCompositeKey(heroGame, 'hero-composite-mobile-hero')} className="composite-mobile__hero">
            {heroCard}
          </GridItem>,
          <GridItem key={getCompositeKey(tallGame, 'hero-composite-mobile-wide')} className="composite-mobile__wide">
            {wideCard}
          </GridItem>,
        ];

    return (
      <Grid columns={1} gap="s" className="game-grid game-grid--composite-mobile">
        {mobileItems}
      </Grid>
    );
  }

  return (
    <Grid
      columns={3}
      gap="s"
      className={joinStrings([
        'game-grid',
        'game-grid--hero-plus-tall',
        reverse && 'game-grid--hero-plus-tall--reverse',
      ])}
    >
      <GridItem key={getCompositeKey(heroGame, 'hero-plus-tall-hero')} className="hero-plus-tall__hero">
        {renderGameCard(heroGame, GameCardContainerType.Box, 'hero-plus-tall-hero', props)}
      </GridItem>
      <GridItem key={getCompositeKey(tallGame, 'hero-plus-tall-tall')} className="hero-plus-tall__tall">
        {renderGameCard(tallGame, GameCardContainerType.Tall, 'hero-plus-tall-tall', props)}
      </GridItem>
    </Grid>
  );
};
